import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import {useSnackbar} from 'notistack'
import {Link, useParams} from 'react-router-dom'
import {ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import {
  ButtonModalConfigEveryone,
  StyleBoxItemCamEveryone,
  StyleBoxItemCamSchedule,
  StyleTabsModal,
  SxBoxListSchedule,
  TitleModalConfig,
} from 'src/pages/camera-config/components/camera-general-data/styles'
import {CameraConfiguration, Server} from 'src/services/api'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import {isNumber} from 'src/utilities/regexValidation'
import {cameraConfigReceiveEventStyles} from './CameraConfigReceiveEventBase'
import {
  ReceiveModeOptionsEnum,
  daysWeek,
  formatScheduleTime,
  getClientIntegration,
  isHidden,
  receiveMode,
  removeScheduleTime,
} from './CameraConfigReceiveEventFunctions'
import useBrainBoxes from 'src/hooks/api/brain-box/useBrainBoxes'
import useScheduleByCamera from 'src/hooks/api/schedule/useScheduleByCamera'
import useOutputServers from 'src/hooks/api/server/useOutputServers'
import {useEffect, useState} from 'react'
import useSingleClient from 'src/hooks/api/client/useSingleClient'
import useSingleCamera from 'src/hooks/api/camera/useSingleCamera'
import CameraConfigFooter from '../camera-config-footer/CameraConfigFooter'
import useUpdateCamera from 'src/hooks/api/camera/useUpdateCamera'
import {CameraReceiveEventModal} from '../camera-receive-event-modal/CameraReceiveEventModal'

function CameraConfigReceiveEvent() {
  const {enqueueSnackbar} = useSnackbar()

  const [isEditing, setIsEditing] = useState(false)
  const [isOpenReceiveModal, setIsOpenReceiveModal] = useState(false)

  const [cameraConfig, setCameraConfig] = useState<Partial<CameraConfiguration> | null>(null)

  const {id} = useParams<{id?: string}>()
  const {mutateAsync: mutateUpdateCamera, isPending: isLoadingUpdateCamera} = useUpdateCamera()

  const {data: camera} = useSingleCamera({
    onError: (error) => handleErrorWithSnackbar(enqueueSnackbar, error, 'Falha ao tentar obter câmera'),
    cameraId: Number(id),
    params: {includes: ['configuration', 'configuration', 'status', 'server', 'client', 'addressInfo', 'company']},
    enabled: !!id,
  })
  const {data: client} = useSingleClient({
    clientId: camera?.clientId ?? -1,
    enabled: !!camera?.clientId,
  })

  const clientIntegration = getClientIntegration(client)

  const hasReadBrainBoxPermission = ValidateUserPermissions('brainbox:read')

  const handleChangePartition = (e: {target: {value: any}}) => {
    if (isNumber(e.target.value) || e.target.value === '') setCameraConfig({...cameraConfig, partition: e.target.value})
  }

  const {data: brainBoxes} = useBrainBoxes({
    params: {
      includes: ['client'],
      filter: {clientIds: [camera?.clientId ?? -1]},
    },
    enabled: !!camera?.clientId && hasReadBrainBoxPermission,
  })

  const {data: schedulers, refetch: refetchSchedules} = useScheduleByCamera({
    cameraId: camera?.id ?? -1,
    enabled: !!camera && !isOpenReceiveModal,
    onError: (error) => handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao carregar agendamentos'),
  })

  const {data: servers} = useOutputServers({
    params: {
      page: 1,
      pageSize: 100,
      filter: {integrationType: 'SAÍDA', companyIds: [camera?.companyId ?? -1]},
      includes: ['serverType'],
    },
    enabled: !!camera,
  })

  useEffect(() => {
    if (camera) setCameraConfig(camera.configuration)
  }, [camera])

  const outputServers: Server[] = [{id: -1, name: 'Sem Integração', ip: '', serverType: {}}, ...(servers?.entities ?? [])]

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', rowGap: '20px'}}>
      <CameraReceiveEventModal camera={camera} isOpenReceiveModal={isOpenReceiveModal} setIsOpenReceiveModal={setIsOpenReceiveModal} />

      <Box overflow='auto' height='55vh' paddingTop='30px'>
        <Box sx={{display: 'flex', flexDirection: 'row'}}>
          <Grid container direction='column' justifyContent='flex-start' spacing={1.5}>
            <Grid item xs={12}>
              <Box sx={[cameraConfigReceiveEventStyles.gridPaper, {display: 'flex', flexDirection: 'row'}]}>
                <Box>
                  <TitleModalConfig>Modo de Controle Arme/Desarme</TitleModalConfig>
                  <Box sx={StyleBoxItemCamEveryone}>
                    <FormControl disabled={!isEditing}>
                      <RadioGroup
                        aria-labelledby='demo-controlled-radio-buttons-group'
                        name='controlled-radio-buttons-group'
                        value={cameraConfig && cameraConfig.receiveModeId}
                        onChange={(_, value) => setCameraConfig({receiveModeId: receiveMode(+value)?.receiveMode.id!})}>
                        {outputServers.filter((server) => server.id !== -1).length > 0 && (
                          <Box sx={{display: 'flex', flexDirection: 'row', gap: '8px'}}>
                            <FormControlLabel
                              value={ReceiveModeOptionsEnum.CLIENT}
                              control={<Radio />}
                              label={<Typography sx={StyleTabsModal}>Controle pelo Cliente</Typography>}
                            />
                            {(clientIntegration === 'sigma' ||
                              clientIntegration === 'moni' ||
                              (cameraConfig?.receiveModeId === ReceiveModeOptionsEnum.CLIENT && clientIntegration !== 'noIntegration')) && (
                              <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                                <Typography sx={{fontSize: '12px'}}>Partição</Typography>
                                <TextField
                                  sx={{width: '2.75em'}}
                                  inputProps={{maxLength: 4}}
                                  disabled={!isEditing}
                                  variant={'standard'}
                                  value={cameraConfig && cameraConfig.partition}
                                  onChange={handleChangePartition}
                                />
                              </Box>
                            )}
                          </Box>
                        )}
                        <FormControlLabel
                          value={ReceiveModeOptionsEnum.HOUR}
                          control={<Radio />}
                          label={<Typography sx={StyleTabsModal}>Controle pelo Horário</Typography>}
                        />
                        {brainBoxes?.entities && brainBoxes?.entities.length > 0 && (
                          <FormControlLabel
                            value={ReceiveModeOptionsEnum.BRAIN_BOX}
                            control={<Radio />}
                            label={<Typography sx={StyleTabsModal}>Controle pela Brain Box</Typography>}
                          />
                        )}
                        <FormControlLabel
                          value={ReceiveModeOptionsEnum.ARMED}
                          control={<Radio />}
                          label={<Typography sx={StyleTabsModal}>Armado 24 Horas</Typography>}
                        />
                      </RadioGroup>
                    </FormControl>
                    {clientIntegration === 'noIntegration' && cameraConfig?.receiveModeId === ReceiveModeOptionsEnum.CLIENT && (
                      <Typography sx={{fontSize: '15px', paddingTop: '5px', paddingLeft: '3px'}}>
                        Este cliente não possui integração de saída configurada para ele, por favor ajuste o cadastro clicando{' '}
                        <Link to={`/registers/clients/clients?id=${camera?.clientId!}`} target='_blank' rel='noopener noreferrer'>
                          aqui
                        </Link>
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Grid>
          <Grid item xs={12} hidden={isHidden(cameraConfig ?? null)}>
            <Box sx={[cameraConfigReceiveEventStyles.gridPaper, {display: 'flex', flexDirection: 'row'}]}>
              {cameraConfig !== null && cameraConfig.receiveModeId === ReceiveModeOptionsEnum.BRAIN_BOX && (
                <Box sx={{display: 'flex', flexDirection: 'column', width: '20%'}}>
                  <TitleModalConfig>Brain Box</TitleModalConfig>
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel id='demo-simple-select-label'>Selecione uma Brain Box</InputLabel>
                      <Select
                        labelId='demo-simple-select-label'
                        id='demo-simple-select'
                        disabled={!isEditing}
                        value={cameraConfig !== null && cameraConfig.brainBoxId}
                        label='Selecione uma Brain Box'
                        onChange={(e) => setCameraConfig({brainBoxId: Number(e.target.value)})}>
                        {brainBoxes?.entities
                          ?.filter((it) => it.client?.id === camera!.client.id)
                          .map((it2) => (
                            <MenuItem key={'server-' + Number(it2.id)} value={Number(it2.id)}>
                              {it2.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              )}
              <Box sx={{width: '100%'}} hidden={cameraConfig !== null && cameraConfig.receiveModeId !== ReceiveModeOptionsEnum.HOUR}>
                <TitleModalConfig>Períodos de Recebimento</TitleModalConfig>
                <Box sx={StyleBoxItemCamSchedule}>
                  <Box
                    sx={{
                      border: '1px solid black',
                      overflowY: 'auto',
                      height: '200px',
                    }}>
                    {schedulers?.map((schedule, index) => (
                      <Box sx={SxBoxListSchedule} key={`schedule-${index}-${schedule.id}`}>
                        <List>
                          <ListItem
                            sx={{p: 0, paddingLeft: 1}}
                            secondaryAction={
                              <IconButton
                                edge='end'
                                aria-label='delete'
                                onClick={async () => {
                                  if (await removeScheduleTime(schedule.id, enqueueSnackbar)) refetchSchedules()
                                }}>
                                <DeleteIcon />
                              </IconButton>
                            }>
                            <ListItemText
                              primary={<Typography sx={StyleTabsModal}>{formatScheduleTime(schedule)}</Typography>}
                              secondary={
                                <Typography sx={StyleTabsModal}>
                                  {daysWeek.map((_, index) => {
                                    if (schedule.scheduleHasDays.find((it) => it.dayOfWeekId === index + 1)) {
                                      return daysWeek[index]
                                    } else {
                                      return '-'
                                    }
                                  })}
                                </Typography>
                              }
                            />
                          </ListItem>
                        </List>
                      </Box>
                    ))}
                  </Box>
                  <Box>
                    <ButtonModalConfigEveryone disabled={!isEditing} onClick={() => setIsOpenReceiveModal(true)}>
                      <AddCircleOutlineIcon sx={{width: 24, height: 24}} />
                      Adicionar
                    </ButtonModalConfigEveryone>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <CameraConfigFooter
        isEditing={isEditing}
        onEdit={() => {
          setIsEditing(true)
        }}
        isLoading={isLoadingUpdateCamera}
        onCancel={() => {
          setIsEditing(false)
          if (camera) setCameraConfig(camera.configuration)
        }}
        onSubmit={async () => {
          if (!camera || !cameraConfig) return
          await mutateUpdateCamera({
            cameraId: camera.id,
            cameraConfig: cameraConfig,
          })
          setIsEditing(false)
        }}
      />
    </Box>
  )
}

export default CameraConfigReceiveEvent
